import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from './icons/ChevronDownIcon'
import { POPOVER_OPEN_EVENT } from '../lib/popoverBus'

export interface MultiSelectOption {
  value: string
  label: string
}

export interface MultiSelectFilterProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (values: string[]) => void
  placeholder: string
  ariaLabel: string
  searchPlaceholder?: string
  /** Label shown on the trigger when nothing is selected (the "all pass" state).
   *  When omitted the trigger shows `placeholder` instead. */
  allLabel?: string
}

export function MultiSelectFilter({
  options,
  selected,
  onChange,
  placeholder,
  ariaLabel,
  searchPlaceholder,
  allLabel,
}: MultiSelectFilterProps) {
  const { t } = useTranslation()
  const instanceId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Tracks when the user explicitly deselected everything via the button.
  // Without this, onChange([]) always restores allPassThrough (all-checked visual)
  // because [] is the sentinel for "all pass through" when allLabel is set.
  const [explicitlyEmpty, setExplicitlyEmpty] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen])

  useEffect(() => {
    function handleOtherPopoverOpen(e: Event) {
      const openedId = (e as CustomEvent<string>).detail
      if (openedId !== instanceId) setIsOpen(false)
    }
    window.addEventListener(POPOVER_OPEN_EVENT, handleOtherPopoverOpen)
    return () => window.removeEventListener(POPOVER_OPEN_EVENT, handleOtherPopoverOpen)
  }, [instanceId])

  function toggleOpen() {
    setIsOpen(prev => {
      const next = !prev
      if (next) {
        window.dispatchEvent(new CustomEvent(POPOVER_OPEN_EVENT, { detail: instanceId }))
        setQuery('')
      }
      return next
    })
  }

  const filteredOptions = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  // When allLabel is provided the component uses an "uncheck-to-exclude" model:
  // selected=[] means "all pass through" and all checkboxes appear checked —
  // unless the user explicitly deselected everything (explicitlyEmpty=true).
  // Without allLabel (e.g. audit log) the classic "check-to-include" model applies.
  const allPassThrough = allLabel !== undefined && selected.length === 0 && !explicitlyEmpty

  function toggleValue(value: string) {
    if (allPassThrough) {
      // Unchecking one item from "all" → select every other option explicitly
      onChange(options.filter(o => o.value !== value).map(o => o.value))
    } else if (selected.includes(value)) {
      const next = selected.filter(v => v !== value)
      // Unchecking the last item would produce onChange([]) = allPassThrough,
      // which snaps all boxes back to checked. Flag it so the display stays empty.
      if (next.length === 0 && allLabel !== undefined) setExplicitlyEmpty(true)
      onChange(next)
    } else {
      setExplicitlyEmpty(false)
      const next = [...selected, value]
      // If all options are now selected, collapse back to the "all pass" sentinel
      onChange(allLabel !== undefined && next.length === options.length ? [] : next)
    }
  }

  function selectAll() {
    setExplicitlyEmpty(false)
    // When all already pass through, explicitly selecting adds nothing —
    // and applying the collapse would incorrectly narrow an "all" to the
    // visible filtered subset.
    if (allPassThrough) return
    const next = [...new Set([...selected, ...filteredOptions.map(o => o.value)])]
    // Collapse to the sentinel when everything is covered (allLabel filters only)
    onChange(allLabel !== undefined && next.length === options.length ? [] : next)
  }

  function deselectAll() {
    if (allPassThrough) {
      const toRemove = new Set(filteredOptions.map(o => o.value))
      const next = options.filter(o => !toRemove.has(o.value)).map(o => o.value)
      // When deselecting all visible items leaves nothing, set explicitlyEmpty so
      // the component stays visually "all unchecked" instead of flipping back to
      // allPassThrough (which would re-check everything via the [] sentinel).
      if (next.length === 0) setExplicitlyEmpty(true)
      onChange(next)
    } else {
      const toRemove = new Set(filteredOptions.map(o => o.value))
      const next = selected.filter(v => !toRemove.has(v))
      if (next.length === 0 && allLabel !== undefined) setExplicitlyEmpty(true)
      onChange(next)
    }
  }

  const allSelected = options.length > 0 && selected.length === options.length
  const selectedLabel = selected.length === 1
    ? options.find(o => o.value === selected[0])?.label
    : undefined
  const triggerLabel =
    selected.length === 0 || allSelected
      ? (allLabel ?? placeholder)
      : (selectedLabel ?? t('auditLog.filters.nSelected', { count: selected.length }))

  return (
    <div ref={containerRef} className="msel">
      <button
        type="button"
        className="msel-trigger"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={toggleOpen}
      >
        <span className="msel-label">{triggerLabel}</span>
        <ChevronDownIcon size={12} />
      </button>
      {isOpen && (
        <div className="msel-pop open">
          <input
            type="text"
            className="form-input msel-search"
            placeholder={searchPlaceholder ?? t('auditLog.filters.searchOptions')}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="msel-actions">
            <button type="button" onClick={selectAll}>{t('auditLog.filters.selectAll')}</button>
            <button type="button" onClick={deselectAll}>{t('auditLog.filters.deselectAll')}</button>
          </div>
          <div className="msel-list">
            {filteredOptions.length === 0 ? (
              <div style={{ padding: 8, color: 'var(--text-tertiary)', fontSize: 12 }}>
                {t('auditLog.noEntries')}
              </div>
            ) : (
              filteredOptions.map(o => (
                <label key={o.value} className="msel-opt">
                  <input
                    type="checkbox"
                    checked={allPassThrough || selected.includes(o.value)}
                    onChange={() => toggleValue(o.value)}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  {o.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
